import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, Italic, List, ListOrdered, Heading3 } from "lucide-react";

const EMPTY = "<p></p>";
const norm = (html) => (html === EMPTY ? "" : html);

export function RichTextEditor({ value, onChange, testid }) {
  const editor = useEditor({
    immediatelyRender: false,
    extensions: [StarterKit.configure({ heading: { levels: [3] } })],
    content: value || "",
    onUpdate: ({ editor }) => onChange(norm(editor.getHTML())),
    editorProps: {
      attributes: {
        class: "min-h-[130px] p-3 text-sm focus:outline-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_h3]:text-base [&_h3]:font-bold [&_h3]:mt-2 [&_p]:my-1",
        "data-testid": testid || "rich-text-editor",
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    if ((value || "") !== norm(editor.getHTML())) editor.commands.setContent(value || "", false);
  }, [value, editor]);

  if (!editor) return null;

  const Btn = ({ active, onClick, label, children }) => (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      className={"h-8 w-8 flex items-center justify-center border-r border-border transition-colors " + (active ? "bg-primary text-primary-foreground" : "hover:bg-secondary")}
      aria-label={label}
      data-testid={(testid || "rte") + "-" + label}
    >
      {children}
    </button>
  );

  return (
    <div className="border border-border rounded-none mt-1 bg-background">
      <div className="flex border-b border-border bg-secondary/40">
        <Btn label="heading" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="h-4 w-4" /></Btn>
        <Btn label="bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="h-4 w-4" /></Btn>
        <Btn label="italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="h-4 w-4" /></Btn>
        <Btn label="bullet-list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="h-4 w-4" /></Btn>
        <Btn label="ordered-list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></Btn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
